import { ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { useI18N } from "@sundae/react-hooks";
import {
  Avatar,
  Button,
  FlexListItem,
  HoverCard,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { IAvatarProps } from "@sundae/ui-toolkit/dist/types/components/atoms/Avatar/Avatar";
import { IHoverCardProps } from "@sundae/ui-toolkit/dist/types/components/organisms/HoverCard/HoverCard";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import { FC, useMemo, useState } from "react";

import { IMappedAsset } from "@sundae/wallet-react";
import { CARDANO_EXPLORER_LINK_MAP, readableAdaAssetId } from "../../constants/cardano.constants";
import { PoolFragmentFragment } from "../../gql/generated/stats2.sdk";
import useAppNavigate from "../../hooks/router/useAppNavigate";
import TransWithCardanoRegistryLink from "../../i18n/components/warnings/WarningWithCardanoRegistryLink/TransWithCardanoRegistryLink.trans";
import { Actions, useAppContext, useProfileAppContext } from "../../stores";
import { IAssetMetaData } from "../../types/Asset.types";
import {
  getAssetId,
  getAssetLogo,
  getAssetName,
  getAssetPolicyIdFromMetadata,
  isAssetIndivisible,
  isAssetRegistered,
} from "../../utils/assets.utils";
import { locationFactories } from "../../utils/routes.utils";
import AssetMetaDataWarning from "../AssetMetaDataWarning";
import CardanoExplorer from "../CardanoExplorer";

export interface IAssetAvatarProps extends Omit<IAvatarProps, "alt" | "src" | "pointer"> {
  asset: IAssetMetaData;
  assetAmount?: IMappedAsset<IAssetMetaData>;
  disabledPopover?: boolean;
  hoverCardProps?: IHoverCardProps;
  privacyEnabled?: boolean;
  contextualPool?: PoolFragmentFragment;
}

const renderVerifiedTooltip = ({ asset }: Pick<IAssetAvatarProps, "asset">) => {
  if (isAssetRegistered(asset) && !isAssetIndivisible(asset)) {
    return (
      <Tooltip
        primitiveProps={{ trigger: { asChild: true } }}
        tooltipLabel={<TransWithCardanoRegistryLink i18nKey="registeredAsset" ns="common" />}
        trigger={
          <div>
            <CheckBadgeIcon className={cx("h-4 w-4", toolkitCx.layout.text.success)} />
          </div>
        }
      />
    );
  }

  return <AssetMetaDataWarning warning={asset.warning} />;
};

const AssetAvatar: FC<IAssetAvatarProps> = ({
  asset,
  assetAmount,
  hoverCardProps,
  privacyEnabled,
  contextualPool,
  disabledPopover = false,
  ...avatarProps
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useI18N();
  const navigate = useAppNavigate();
  const {
    state: { cardanoExplorer },
  } = useProfileAppContext();
  const { dispatch: appDispatch } = useAppContext();

  const assetLogo = getAssetLogo(asset, privacyEnabled) || "";
  const assetName = getAssetName(asset, 20, 0);

  const memoizedHoverCardItems = useMemo(() => {
    return [
      {
        id: 1,
        label: t("ticker", { ns: "assets" }),
        value: asset.ticker,
      },
      {
        id: 2,
        label: t("decimals", { ns: "assets" }),
        value: String(asset.decimals),
      },
      {
        id: 3,
        label: t("policyId", { ns: "assets" }),
        value: (
          <CardanoExplorer
            size="sm"
            type={CARDANO_EXPLORER_LINK_MAP.tokenPolicy[cardanoExplorer]}
            hash={getAssetPolicyIdFromMetadata(asset)}
          />
        ),
      },
      {
        id: 4,
        label: t("registered", { ns: "assets" }),
        value: t(isAssetRegistered(asset) ? "yes" : "no"),
      },
      {
        id: 5,
        label: t("divisible", { ns: "assets" }),
        value: t(isAssetIndivisible(asset) ? "no" : "yes"),
      },
    ];
  }, [asset, cardanoExplorer, t]);

  if (disabledPopover) {
    return <Avatar {...avatarProps} alt={assetName} src={assetLogo} pointer zoomed />;
  }

  if (SundaeUtils.isAdaAsset(asset)) {
    return <Avatar {...avatarProps} alt={assetName} src={assetLogo} />;
  }

  return (
    <HoverCard
      {...hoverCardProps}
      open={open}
      onOpenChange={setOpen}
      primitiveProps={{
        content: {
          ...hoverCardProps?.primitiveProps?.content,
          className: cx(
            "min-w-[17.5rem]",
            "md:min-w-[20rem]",
            hoverCardProps?.primitiveProps?.content?.className
          ),
        },
        trigger: {
          ...hoverCardProps?.primitiveProps?.trigger,
          asChild: true,
        },
      }}
      trigger={
        <div
          className={cx("relative flex flex-wrap items-center gap-2 rounded-full", {
            "motion-safe:animate-shadow-pulse": open,
          })}
        >
          <Avatar {...avatarProps} alt={assetName} src={assetLogo} pointer zoomed />
        </div>
      }
    >
      <div className="relative z-10 flex h-full w-full items-center gap-2 motion-safe:animate-slide-down">
        <div className="grid w-full will-change-transform" key="AssetAvatar--Content">
          <div className="flex items-center gap-2 will-change-transform" key="AssetAvatar--Avatar">
            <Avatar size="md" src={assetLogo} alt={assetName} />
            <Text tag="span" size="sm">
              {assetName}
            </Text>
            {renderVerifiedTooltip({ asset })}
          </div>

          {asset.description && (
            <div className="mt-3 w-full">
              <Text size="sm" variant="muted" tag="p">
                {asset.description}
              </Text>
            </div>
          )}

          <div className="mt-3 w-full will-change-transform">
            <Text tag="h3">{t("information", { ns: "assets" })}</Text>

            <ul className="mt-2 flex w-full flex-col gap-2 will-change-transform">
              {memoizedHoverCardItems.map(({ id, label, value }, index) => {
                return (
                  <li className="will-change-transform" key={`${index}-${id}`}>
                    <FlexListItem field={{ label, variant: "muted" }} value={{ label: value }} />
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-3 flex w-full flex-nowrap justify-between gap-3 will-change-transform">
            {contextualPool && assetAmount && (
              <Button
                className="flex-1"
                onClick={() => {
                  Actions.setActiveZapAsset(assetAmount, appDispatch);
                  Actions.setActiveZapPool(contextualPool, appDispatch);
                  Actions.setDialogState({ dialog: "liquidity.zap", isOpen: true }, appDispatch);
                }}
                size="sm"
                variant="primary"
              >
                {t("liquidity", { context: "zap", ns: "liquidity" })}
              </Button>
            )}

            <Button
              className="flex-1"
              onClick={() =>
                navigate(
                  locationFactories.exchange.swap({
                    given: getAssetId(asset),
                    taken: readableAdaAssetId,
                  })
                )
              }
              size="sm"
              variant="neutral"
            >
              <ArrowsRightLeftIcon className="h-4 w-4" />
              {t("trade", { context: "asset", ns: "common" })}{" "}
            </Button>
          </div>
        </div>
      </div>
    </HoverCard>
  );
};

export default AssetAvatar;
